import { InfoKeepCard } from '../components/InfoKeepCard'
import { colors } from 'app-v2/design-systems/theme'
import { WinningDescription } from './components/WinningDescription'
import { Button } from 'app-v2/design-systems/components/inputs/button'
import { useIntl } from 'react-intl'
import React, { useCallback, useState } from 'react'
import { DoneModal } from './components/modals/DoneModal'
import { useDispatch, useSelector } from 'react-redux'
import { useTPKState } from 'shared/store/tu-preferes-king'
import { setSigninRedirect } from 'shared/store/signin'
import { useParams } from 'react-router-dom/cjs/react-router-dom'
import { Redirect, useHistory } from 'react-router-dom'
import type { WinningType } from '../types'
import { push } from 'connected-react-router'
import type { RootState } from 'shared/store/reducers'
import { Text } from '../../common/typography'
import styled from 'styled-components'
import { TPKLayout } from '../components/TPKLayout'
import { CardWithWinning } from '../components/CardWithWinning'
import { SIZE } from 'app-v2/design-tokens'
import { Spacer } from 'app-v2/design-systems'
import { StyledText } from '../components/styles'

function GetGiftButton({
    tpkToken,
    logged,
    needLogged,
    alreadyCollected,
    openDoneModal,
    hasCouponCode
}: {
    tpkToken: string,
    logged: boolean,
    needLogged: boolean,
    alreadyCollected: boolean,
    openDoneModal: () => void,
    hasCouponCode: boolean
}) {
    const intl = useIntl()
    const history = useHistory()

    const redirectToMail = () => {
        history.replace(
            intl.formatMessage(
                { id: 'routes.tupreferesking.email.tpkToken' },
                {
                    tpkToken
                }
            )
        )
    }

    const redirectToQrCode = () => {
        history.replace(
            intl.formatMessage(
                { id: 'routes.tupreferesking.qrCode.tpkToken' },
                {
                    tpkToken
                }
            )
        )
    }

    const dispatch = useDispatch()
    const onClick = useCallback(() => {
        if (alreadyCollected) {
            hasCouponCode ? redirectToQrCode() : openDoneModal()
        } else if (needLogged && !logged) {
            const redirectTo: string = intl.formatMessage(
                {
                    id: 'routes.tupreferesking.duel.win.tpkToken'
                },
                { tpkToken }
            )
            dispatch(setSigninRedirect(redirectTo))
            dispatch(
                push(
                    intl.formatMessage({
                        id: 'routes.signup'
                    })
                )
            )
        } else {
            redirectToMail()
        }
    }, [alreadyCollected, needLogged, logged, tpkToken])

    if (needLogged && !logged) {
        return (
            <Button
                buttonColor={colors.meltyYellow}
                labelColor={colors.white}
                onClick={onClick}>
                {intl.formatMessage({
                    id: 'tupreferesking.win.signin-get-gift'
                })}
            </Button>
        )
    }

    return (
        <Button
            buttonColor={colors.bbqBrown}
            labelColor={colors.white}
            onClick={onClick}>
            {intl.formatMessage({
                id: 'tupreferesking.win.get-gift'
            })}
        </Button>
    )
}

export function TuPreferesKingDuelWin() {
    const intl = useIntl()
    const { tpkToken } = useParams()
    const [modalDoneVisible, setModalDoneVisible] = useState(true)

    const data = useTPKState(tpkToken, { autoload: false })
    const isLogged = useSelector((s: RootState) => s.userState.authenticated)
    if (
        !data.duel ||
        (data.duel &&
            !data.duel.childGain.selected &&
            !data.duel.parentGain.selected)
    ) {
        return (
            <Redirect
                to={intl.formatMessage(
                    { id: 'routes.tupreferesking.duel.tpkToken' },
                    {
                        tpkToken
                    }
                )}
            />
        )
    }
    const duel = data.duel
    if (
        (duel.childGain.selected || duel.parentGain.selected) &&
        duel.email &&
        duel.couponCode
    ) {
        return (
            <Redirect
                to={intl.formatMessage(
                    { id: 'routes.tupreferesking.qrCode.tpkToken' },
                    {
                        tpkToken
                    }
                )}
            />
        )
    }
    const type: WinningType = duel.childGain.selected ? 'CHILD' : 'PARENT'
    const winning = duel.childGain.selected ? duel.childGain : duel.parentGain

    return (
        <TPKLayout
            header={{
                title: intl.formatMessage({
                    id: 'tupreferesking.win.layout.title'
                }),
                subTitle: intl.formatMessage({
                    id: 'tupreferesking.win.layout.sub-title'
                }),
                largeBackground: true
            }}>
            <StyledCardContainer>
                <CardWithWinning
                    type={type}
                    winning={winning}
                    selected
                    withConfettis>
                    <WinningDescription winning={winning} type={type} small />

                    <Spacer size={SIZE.XL} />

                    <StyledText>
                        {intl.formatMessage({
                            id: winning.kingdomRequired
                                ? 'tupreferesking.win.description.logged'
                                : 'tupreferesking.win.description'
                        })}
                    </StyledText>

                    {data.error && (
                        <Text color={colors.fieryRed}>
                            {intl.formatMessage({
                                id: `tupreferesking.errors.${data.error}`
                            })}
                        </Text>
                    )}

                    <Spacer size={SIZE.XL} />

                    <GetGiftButton
                        alreadyCollected={!!duel.email}
                        needLogged={winning.kingdomRequired}
                        logged={isLogged}
                        tpkToken={tpkToken}
                        openDoneModal={() => setModalDoneVisible(true)}
                        hasCouponCode={!!duel.couponCode}
                    />

                    <DoneModal
                        visible={!!duel.email && modalDoneVisible}
                        onClose={() => setModalDoneVisible(false)}
                        selectedWinning={winning}
                        email={duel.email}
                        type={type}
                    />
                </CardWithWinning>
            </StyledCardContainer>

            <Spacer size={SIZE.XXL} />

            <InfoKeepCard />
        </TPKLayout>
    )
}

const StyledCardContainer = styled.div`
    max-height: 400px;
`
